import Link from 'next/link';
import { ArrowRight } from 'lucide-react';

export interface StoneGuideRelatedItem {
  id: string;
  slug: string;
  title: string;
  description: string;
  categoryLabel: string;
}

export interface StoneGuideRelatedProps {
  guides: StoneGuideRelatedItem[];
  locale: string;
  title?: string;
}

export function StoneGuideRelated({ guides, locale, title }: StoneGuideRelatedProps) {
  if (guides.length === 0) return null;

  const sectionTitle = title ?? (locale === 'fr' ? 'Guides associés' : 'Related guides');

  return (
    <section className="py-16 md:py-20 border-t border-stone-200">
      <div className="container mx-auto max-w-7xl px-6 md:px-12">
        <h2
          className="font-serif text-3xl md:text-4xl text-stone-900 italic mb-10"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
        >
          {sectionTitle}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {guides.slice(0, 3).map((related) => (
            <Link
              key={related.id}
              href={`/${locale}/guides/${related.slug}`}
              className="group bg-white p-8 shadow-lg hover:shadow-xl transition-shadow flex flex-col h-full"
            >
              <span className="text-[10px] uppercase tracking-[0.3em] text-stone-400 font-sans mb-4">
                {related.categoryLabel}
              </span>
              <h3
                className="font-serif text-xl md:text-2xl text-stone-900 italic mb-3 group-hover:text-stone-700 transition-colors"
                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
              >
                {related.title}
              </h3>
              <p className="font-sans text-stone-600 font-light text-sm leading-relaxed flex-1 line-clamp-3">
                {related.description}
              </p>
              <span className="inline-flex items-center gap-2 mt-6 text-[10px] uppercase tracking-[0.2em] text-stone-900 font-sans group-hover:gap-3 transition-all">
                {locale === 'fr' ? 'Lire le guide' : 'Read guide'}
                <ArrowRight size={14} strokeWidth={1.5} aria-hidden="true" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export default StoneGuideRelated;
